import { useMemo } from 'react';
import { alpha, Box, Container } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import DrawerNavigation from 'src/shared/components/navigations/drawerNavigation';
import NotificationFeed from 'src/shared/components/organisms/notificationFeed';
import ProfileFeed from 'src/shared/components/organisms/profileFeed';
import GroupListFeed from 'src/shared/components/organisms/groupListFeed';
import { ScrollToTop } from 'src/shared/components/atoms/scrollToTop';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useSelector } from 'react-redux';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { SxProps, Theme, ThemeProvider } from '@mui/material/styles';
import { Colors } from 'src/shared/components/styles';
import {
  createAppTheme,
  FEED_WIDTH,
  MAIN_CONTENT_WIDTH,
  NAV_WIDTH,
  SHELL_MAX_WIDTH,
  SHELL_TOP_GAP,
} from 'src/shared/theme/createAppTheme';

interface AppTemplateProps {
  mainNavigation: JSX.Element;
  mainContent: JSX.Element;
  showNotificationFeed: boolean;
  setShowNotificationFeed: (arg: boolean) => void;
  showProfileFeed: boolean;
  setShowProfileFeed: (arg: boolean) => void;
  showGroupList: boolean;
  setShowGroupList: (arg: boolean) => void;
  showMenu: boolean;
}

toast.configure({
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
});

export const AppTemplate = (props: AppTemplateProps) => {
  const {
    mainNavigation,
    mainContent,
    showNotificationFeed,
    setShowNotificationFeed,
    showProfileFeed,
    setShowProfileFeed,
    showGroupList,
    setShowGroupList,
    showMenu,
  } = props;

  const { userCompany } = useSelector(
    (state: RootStateType) => state.authState,
  );

  const theme = useMemo(
    () => createAppTheme(userCompany?.color),
    [userCompany],
  );

  const loading = false;
  const rightFeedOpen =
    showGroupList || showProfileFeed || showNotificationFeed;
  const appBarOffset = (theme.mixins.toolbar.minHeight ?? 64) as number;

  // Fullbreddsyta som centerar inenhålll
  const chellCenterXs = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  } as const;
  // Håller en maxbredd för att flytta "dödytor" i kanten
  const shellInnerSx = {
    width: '100%',
    maxWidth: SHELL_MAX_WIDTH,
    display: 'flex',
    alignItems: 'flex-start',
    gap: { lg: 2 },
    px: { xs: 0, lg: 2 },
    mt: { xs: 0, lg: `${SHELL_TOP_GAP}px` },
  } as const;
  // Vänster kolumn/meny
  const leftRailSx: SxProps<Theme> = {
    display: { xs: 'none', lg: 'block' },
    width: { xs: '100%', lg: NAV_WIDTH },
    flexShrink: 0,
    boxSizing: 'border-box',
    borderRight: `1px solid ${alpha(Colors.GRAY_DARK, 0.08)}`,
    backgroundColor: 'white',
    borderRadius: 1,
    position: 'sticky',
    top: `calc(${appBarOffset}px + ${SHELL_TOP_GAP}px)`,
    height: `calc(100dvh - ${appBarOffset}px - ${SHELL_TOP_GAP * 2}px)`,
    overflowX: 'hidden',
    overflowY: 'auto',
    alignSelf: 'flex-start',
  } as const;
  // Mitten content
  const mainSx = {
    flex: 1,
    minWidth: 0,
    display: { xs: showMenu ? 'none' : 'block', lg: 'block' },
    height: `calc(100dvh - ${appBarOffset}px - ${SHELL_TOP_GAP * 2}px)`,
    boxSizing: 'border-box',
    overflowY: 'auto',
    pb: 3,
    mt: {
      xs: `calc(${appBarOffset}px + ${SHELL_TOP_GAP}px)`,
      lg: `calc(${appBarOffset}px)`,
    },
  } as const;
  // Höger kolumn (notiser/grupper/profil)
  const rightRailSx: SxProps<Theme> = {
    backgroundColor: 'white',
    position: { xs: 'relative', lg: 'sticky' },
    top: { lg: `calc(${appBarOffset}px + ${SHELL_TOP_GAP}px)` },
    width: { xs: '100%', lg: FEED_WIDTH },
    height: `calc(100dvh - ${appBarOffset}px - ${SHELL_TOP_GAP * 2}px)`,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    pt: { lg: 1 },
    overflowY: { lg: 'auto' },
    overflowX: 'hidden',
    // Keep the rail under the app bar and modals.
    zIndex: (t) => t.zIndex.appBar - 1,
    borderLeft: { lg: `1px solid ${alpha(Colors.GRAY_DARK, 0.08)}` },
    borderRadius: 1,
    alignSelf: 'flex-start',
  } as const;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position='top-right'
        style={{ top: `${appBarOffset + 8}px` }}
      />
      <ScrollToTop />

      {mainNavigation}

      {/* Shell: centered content with left/right rails */}
      <Box sx={chellCenterXs}>
        <Box
          sx={shellInnerSx}
          data-right-rail={rightFeedOpen ? 'open' : 'closed'}
        >
          {/* Left navigation (desktop only) */}
          <Box sx={leftRailSx}>
            {/* No overflowY here — avoids the 2nd scrollbar.
                If DrawerNavigation needs scrolling, it should do it internally. */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DrawerNavigation />
            </Box>
          </Box>

          {/* Main content pane (independent scroll) */}
          <Box component='main' sx={mainSx}>
            <Container
              maxWidth={false}
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '100%',
                  lg: rightFeedOpen ? MAIN_CONTENT_WIDTH : '100%',
                },
                mx: 0,
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    mt: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress color='secondary' />
                </Box>
              ) : (
                mainContent
              )}
            </Container>
          </Box>

          {/* Right rail */}
          {rightFeedOpen && (
            <Box sx={rightRailSx}>
              {/* On small screens the rail is in normal flow, so add an AppBar spacer. */}
              <Box
                sx={(t) => ({
                  ...t.mixins.toolbar,
                  display: { xs: 'block', lg: 'none' },
                })}
              />
              <Box
                sx={{
                  flex: 1,
                }}
              >
                {showNotificationFeed && (
                  <NotificationFeed
                    setShowNotificationFeed={setShowNotificationFeed}
                  />
                )}
                {showProfileFeed && (
                  <ProfileFeed setShowProfileFeed={setShowProfileFeed} />
                )}
                {showGroupList && (
                  <GroupListFeed
                    setShowGroupList={() => setShowGroupList(false)}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
